import { useState } from 'react';
import { CUDA_LATENCY_LEAD } from '@/content/benchmarks';
import { ACCENT, CONTEXT, GRID, INK_MUTED } from './palette';

/**
 * CUDA end to end: how much lower the Fnk AI stack's latency is than the assembled stack's, at each point of the
 * response-time distribution, for both chat scenarios. Relative only — percent better, no seconds.
 */

const W = 560;
const PAD_L = 72;
const PAD_R = 20;
const PAD_T = 12;
const PAD_B = 40;
const BAR = 9;
const GAP = 2;
const ROW = 36;
const TICKS = [0, 25, 50, 75, 100];
const COLORS = [ACCENT, CONTEXT];

const { points, series } = CUDA_LATENCY_LEAD;
const H = PAD_T + points.length * ROW + PAD_B;
const x = (v: number) => PAD_L + (v / 100) * (W - PAD_L - PAD_R);
const best = Math.max(...series.flatMap((s) => s.values));

export function LatencyLeadChart() {
  const [hovered, setHovered] = useState<number | null>(null);

  return (
    <figure className="m-0">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="h-auto w-full"
        role="img"
        aria-label={`Latency lead of the Fnk AI stack over the assembled stack at the median, p75, p95, p99, slowest and average response, for a single question and a saved conversation: between ${Math.min(...series.flatMap((s) => s.values))}% and ${best}% better at every point.`}
      >
        {TICKS.map((t) => (
          <g key={t}>
            <line x1={x(t)} x2={x(t)} y1={PAD_T} y2={H - PAD_B} stroke={GRID} strokeWidth="1" strokeDasharray={t === 0 ? undefined : '2 4'} />
            <text x={x(t)} y={H - PAD_B + 16} textAnchor="middle" fontSize="10" fill={INK_MUTED} fontFamily="IBM Plex Mono, monospace">
              {t === 0 ? 'baseline' : `+${t}%`}
            </text>
          </g>
        ))}
        <text x={(PAD_L + W - PAD_R) / 2} y={H - 6} textAnchor="middle" fontSize="10" fill={INK_MUTED}>
          Lower latency than the assembled stack
        </text>
        {points.map((p, i) => {
          const top = PAD_T + i * ROW + (ROW - series.length * BAR - GAP) / 2;
          const dim = hovered !== null && hovered !== i;
          return (
            <g
              key={p}
              opacity={dim ? 0.35 : 1}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
            >
              <rect x={0} y={PAD_T + i * ROW} width={W} height={ROW} fill="transparent" />
              <text x={PAD_L - 10} y={top + BAR + 4} textAnchor="end" fontSize="10.5" fill={INK_MUTED} fontFamily="IBM Plex Mono, monospace">
                {p}
              </text>
              {series.map((s, j) => (
                <rect key={s.label} x={x(0)} y={top + j * (BAR + GAP)} width={x(s.values[i]) - x(0)} height={BAR} rx="1" fill={COLORS[j]}>
                  <title>{`${p}, ${s.label.toLowerCase()}: ${s.values[i]}% better`}</title>
                </rect>
              ))}
            </g>
          );
        })}
      </svg>

      <p className="mt-2 min-h-[1.5em] font-mono text-[11px] tabular text-ink-2" aria-live="polite">
        {hovered === null
          ? `Up to ${best}% better`
          : `${points[hovered]} — ${series.map((s) => `${s.label.toLowerCase()}: ${s.values[hovered]}% better`).join(' · ')}`}
      </p>

      <div className="mt-3 space-y-2">
        {series.map((s, j) => (
          <div key={s.label} className="flex items-baseline gap-2.5">
            <span className="mt-1.5 h-2 w-4 shrink-0 rounded-sm" style={{ background: COLORS[j] }} aria-hidden="true" />
            <p className="text-[11px] leading-relaxed">
              <span className={j === 0 ? 'font-semibold text-ink' : 'text-ink-2'}>{s.label}</span> <span className="text-ink-4">— {s.sub}</span>
            </p>
          </div>
        ))}
      </div>

      <div className="mt-4 border-t border-line pt-4">
        <p className="text-[13px] leading-relaxed text-ink-2">
          <span className="font-semibold text-ink">The lead holds across the whole distribution.</span> From the median to the slowest response, in both
          scenarios, the Fnk AI stack answers with roughly three quarters less latency — the gain is not an average hiding a slow tail.
        </p>
        <p className="mt-2 text-[11px] leading-relaxed text-ink-3">
          Paired run: same model, same workload, same hardware; three machines, 256 concurrent chat users. Each bar is the latency the Gatling load generator
          measured, relative to the assembled stack at the same point. One run per stack — not a confidence interval.
        </p>
      </div>
    </figure>
  );
}
